import { FC, useMemo } from "react";

import { StudentOutcomeScoreTable } from "./StudentOutcomeScoreTable";
import { StudentScoreOverviewTable } from "./StudentScoreOverviewTable";
import { mapStudentAssessmentScoreTableRow } from "./utils";

import {
  serverGetStudentAssessmentScoreTable,
  serverGetStudentOutcomeScoreTable,
} from "api/api-server-assessment";
import { LoadingTextIndicator } from "components/LoadingScreen";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

type StudentPerformanceDashboardProps = {
  userId: string | null;
  studentName?: string;
};

export const StudentPerformanceDashboard: FC<StudentPerformanceDashboardProps> = ({
  userId,
  studentName,
}) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isViewingOwnAssessments = currentUserId === userId;
  const targetUserId = isViewingOwnAssessments ? null : userId;

  const {
    data: studentPerformanceData,
    isLoading,
    error,
  } = usePromiseEffect(async () => {
    if (!sectionId) {
      return null;
    }

    const [studentAssessmentScoreTableRows, studentOutcomeScoreTableData] = await Promise.all([
      serverGetStudentAssessmentScoreTable(sectionId, targetUserId),
      serverGetStudentOutcomeScoreTable(sectionId, targetUserId),
    ]);

    return { studentAssessmentScoreTableRows, studentOutcomeScoreTableData };
  }, [sectionId, targetUserId]);

  const scoreOverviewTableRows = useMemo(() => {
    if (!studentPerformanceData || error) {
      return [];
    }

    return studentPerformanceData.studentAssessmentScoreTableRows.map(
      mapStudentAssessmentScoreTableRow,
    );
  }, [studentPerformanceData, error]);

  if (isLoading) {
    return <LoadingTextIndicator>{t("common.loading")}</LoadingTextIndicator>;
  }

  if (!sectionId || !studentPerformanceData || error || scoreOverviewTableRows.length === 0) {
    return (
      <EmptyCard
        className="assessment-dashboard mb-4"
        description={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_description"
            : "assessment_dashboard.all.empty_description_instructor",
          {
            studentName: studentName || t("assessment_dashboard.all.unknown_student"),
          },
        )}
        icon={<AssessPointsMarkerIcon className="text-blue" />}
        title={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_title"
            : "assessment_dashboard.all.empty_title_instructor",
        )}
      />
    );
  }

  return (
    <main className="assessment-dashboard overflow-hidden">
      <StudentScoreOverviewTable sectionId={sectionId} tableRows={scoreOverviewTableRows} />

      <StudentOutcomeScoreTable
        sectionId={sectionId}
        tableData={studentPerformanceData.studentOutcomeScoreTableData}
      />
    </main>
  );
};
